'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { getChatCompletion } from '@/lib/ai/chatCompletion';

// ── Types ─────────────────────────────────────────────────────────────────────
type DisputeCategory = 'lost_parcel' | 'damage' | 'non_delivery' | 'fraud' | 'payment' | 'other';
type DisputeStatus = 'open' | 'under_review' | 'resolved_refund' | 'resolved_release' | 'escalated' | 'closed';
type DisputePriority = 'low' | 'medium' | 'high' | 'critical';

interface Dispute {
  id: string;
  category: DisputeCategory;
  reason: string;
  description: string | null;
  status: DisputeStatus;
  priority: DisputePriority;
  amount: number;
  currency: string;
  evidence_urls: string[];
  order_id: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  resolution_note: string | null;
  order_route?: string;
}

interface DisputeNote {
  id: string;
  dispute_id: string;
  author_id: string;
  note: string;
  is_internal: boolean;
  created_at: string;
  author?: { full_name: string };
}

interface Order {
  id: string;
  route: string;
  status: string;
}

interface Delivery {
  id: string;
  route: string;
  status: string;
}

interface AIAnalysis {
  detectedCategory: DisputeCategory;
  confidence: 'high' | 'medium' | 'low';
  summary: string;
  resolutionSteps: string[];
  urgency: 'critical' | 'high' | 'medium' | 'low';
  estimatedResolutionTime: string;
}

// ── Config ────────────────────────────────────────────────────────────────────
const CATEGORY_OPTIONS: { value: DisputeCategory; label: string; icon: string; description: string }[] = [
  { value: 'lost_parcel', label: 'Lost Parcel', icon: 'ArchiveBoxXMarkIcon', description: 'Parcel cannot be located or was never delivered' },
  { value: 'damage', label: 'Damaged Item', icon: 'ExclamationTriangleIcon', description: 'Parcel arrived with visible damage' },
  { value: 'non_delivery', label: 'Non-Delivery', icon: 'TruckIcon', description: 'Traveler claims delivery but sender denies receipt' },
  { value: 'fraud', label: 'Fraud / Scam', icon: 'ShieldExclamationIcon', description: 'Suspicious activity, fake tracking, or identity fraud' },
  { value: 'payment', label: 'Payment Issue', icon: 'BanknotesIcon', description: 'Overcharge, double billing, or escrow dispute' },
  { value: 'other', label: 'Other Issue', icon: 'QuestionMarkCircleIcon', description: 'Any other order-related complaint' },
];

const STATUS_CONFIG: Record<DisputeStatus, { label: string; variant: 'danger' | 'warning' | 'success' | 'muted' | 'info'; step: number }> = {
  open: { label: 'Open', variant: 'danger', step: 0 },
  under_review: { label: 'Under Review', variant: 'warning', step: 1 },
  escalated: { label: 'Escalated', variant: 'danger', step: 2 },
  resolved_refund: { label: 'Refunded', variant: 'success', step: 3 },
  resolved_release: { label: 'Released', variant: 'success', step: 3 },
  closed: { label: 'Closed', variant: 'muted', step: 3 },
};

const PRIORITY_COLOR: Record<DisputePriority, string> = {
  low: 'text-muted-foreground',
  medium: 'text-amber-500',
  high: 'text-danger',
  critical: 'text-danger font-700',
};

// ── Status Pipeline Steps ─────────────────────────────────────────────────────
const PIPELINE_STEPS = [
  { label: 'Filed', icon: 'DocumentTextIcon', desc: 'Dispute submitted' },
  { label: 'Under Review', icon: 'MagnifyingGlassIcon', desc: 'Admin reviewing case' },
  { label: 'Decision Pending', icon: 'ScaleIcon', desc: 'Awaiting resolution' },
  { label: 'Resolved', icon: 'CheckCircleIcon', desc: 'Case closed' },
];

// ── File Upload Helper ────────────────────────────────────────────────────────
function FileUploadZone({
  files,
  onAdd,
  onRemove,
  uploading,
}: {
  files: { name: string; preview: string; file: File }[];
  onAdd: (f: FileList) => void;
  onRemove: (i: number) => void;
  uploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) onAdd(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
          ${dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,video/mp4"
          className="hidden"
          onChange={e => e.target.files && onAdd(e.target.files)}
        />
        <Icon name="PhotoIcon" size={28} className="text-muted-foreground mx-auto mb-2" />
        <p className="text-sm font-600 text-foreground">Drop files here or click to upload</p>
        <p className="text-xs text-muted-foreground mt-1">JPG, PNG, PDF, MP4 · Max 20 MB each · Up to 5 files</p>
      </div>

      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {files.map((f, i) => (
            <div key={i} className="relative group rounded-lg overflow-hidden border border-border bg-muted/20">
              {f.file.type.startsWith('image/') ? (
                <img src={f.preview} alt={f.name} className="w-full h-20 object-cover" />
              ) : (
                <div className="w-full h-20 flex flex-col items-center justify-center gap-1">
                  <Icon name="DocumentIcon" size={24} className="text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground truncate px-2 w-full text-center">{f.name}</span>
                </div>
              )}
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onRemove(i); }}
                disabled={uploading}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-danger text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Icon name="XMarkIcon" size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Case Status Pipeline ──────────────────────────────────────────────────────
function CaseStatusPipeline({ status }: { status: DisputeStatus }) {
  const currentStep = STATUS_CONFIG[status].step;
  const isResolved = ['resolved_refund', 'resolved_release', 'closed'].includes(status);

  return (
    <div className="relative">
      <div className="flex items-start justify-between gap-1">
        {PIPELINE_STEPS.map((step, i) => {
          const isActive = i === currentStep;
          const isDone = i < currentStep || (isResolved && i === currentStep);
          const isPending = i > currentStep;

          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5 relative">
              {/* Connector line */}
              {i < PIPELINE_STEPS.length - 1 && (
                <div className={`absolute top-4 left-1/2 w-full h-0.5 z-0 transition-colors
                  ${isDone ? 'bg-primary' : 'bg-border'}`} />
              )}
              {/* Step circle */}
              <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all
                ${isDone ? 'bg-primary border-primary text-primary-foreground' :
                  isActive ? 'bg-primary/10 border-primary text-primary': 'bg-background border-border text-muted-foreground'}`}>
                {isDone ? (
                  <Icon name="CheckIcon" size={14} />
                ) : (
                  <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={13} />
                )}
              </div>
              {/* Label */}
              <div className="text-center">
                <p className={`text-[10px] font-600 leading-tight
                  ${isActive ? 'text-primary' : isDone ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {step.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── AI Analysis Panel ─────────────────────────────────────────────────────────
function AIAnalysisPanel({
  analysis,
  onApplyCategory,
  onDismiss,
}: {
  analysis: AIAnalysis;
  onApplyCategory: (cat: DisputeCategory) => void;
  onDismiss: () => void;
}) {
  const urgencyColor: Record<string, string> = {
    critical: 'text-danger bg-danger/10 border-danger/30',
    high: 'text-orange-600 bg-orange-50 border-orange-200',
    medium: 'text-amber-600 bg-amber-50 border-amber-200',
    low: 'text-success bg-success/10 border-success/30',
  };
  const confidenceLabel: Record<string, string> = {
    high: '✓ High confidence',
    medium: '~ Medium confidence',
    low: '? Low confidence',
  };
  const confidenceColor: Record<string, string> = {
    high: 'text-success',
    medium: 'text-amber-500',
    low: 'text-muted-foreground',
  };
  const detectedCat = CATEGORY_OPTIONS.find(c => c.value === analysis.detectedCategory);

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-primary/20 bg-primary/10">
        <div className="flex items-center gap-2">
          <Icon name="SparklesIcon" size={16} className="text-primary" />
          <span className="text-sm font-700 text-primary">AI Dispute Analysis</span>
          <span className={`text-[10px] font-600 ${confidenceColor[analysis.confidence]}`}>
            {confidenceLabel[analysis.confidence]}
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 rounded hover:bg-primary/10 text-muted-foreground transition-colors"
        >
          <Icon name="XMarkIcon" size={14} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Detected type */}
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-xs text-muted-foreground mb-1">Detected Dispute Type</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-700 text-foreground">{detectedCat?.label ?? analysis.detectedCategory}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-600 ${urgencyColor[analysis.urgency]}`}>
                {analysis.urgency.charAt(0).toUpperCase() + analysis.urgency.slice(1)} urgency
              </span>
              <span className="text-xs text-muted-foreground">· Est. {analysis.estimatedResolutionTime}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onApplyCategory(analysis.detectedCategory)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-600 hover:opacity-90 transition-opacity flex-shrink-0"
          >
            <Icon name="CheckIcon" size={12} />
            Apply
          </button>
        </div>

        {/* AI summary */}
        <div className="p-3 bg-background rounded-lg border border-border">
          <p className="text-xs font-700 text-foreground mb-1">AI Assessment</p>
          <p className="text-xs text-muted-foreground leading-relaxed">{analysis.summary}</p>
        </div>

        {/* Resolution steps */}
        <div>
          <p className="text-xs font-700 text-foreground mb-2">Suggested Resolution Steps</p>
          <ol className="space-y-2">
            {analysis.resolutionSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-700 flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <p className="text-xs text-foreground leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

// ── Dispute Detail Modal ──────────────────────────────────────────────────────
function DisputeDetailModal({ dispute, onClose }: { dispute: Dispute; onClose: () => void }) {
  const { user } = useAuth();
  const supabase = createClient();
  const isResolved = ['resolved_refund', 'resolved_release', 'closed'].includes(dispute.status);
  const category = CATEGORY_OPTIONS.find(c => c.value === dispute.category);
  const [notes, setNotes] = useState<DisputeNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [activeSection, setActiveSection] = useState<'details' | 'timeline' | 'notes'>('details');

  useEffect(() => {
    const fetchNotes = async () => {
      setLoadingNotes(true);
      const { data } = await supabase
        .from('dispute_notes')
        .select('*, author:author_id(full_name)')
        .eq('dispute_id', dispute.id)
        .eq('is_internal', false)
        .order('created_at', { ascending: true });
      if (data) setNotes(data as DisputeNote[]);
      setLoadingNotes(false);
    };
    fetchNotes();
  }, [dispute.id]);

  // Build timeline events
  const timelineEvents = [
    {
      label: 'Dispute filed',
      time: dispute.created_at,
      icon: 'DocumentTextIcon',
      color: 'text-primary',
      bg: 'bg-primary/10',
      desc: `Category: ${category?.label ?? dispute.category}`,
    },
    ...(dispute.status === 'under_review' || ['escalated', 'resolved_refund', 'resolved_release', 'closed'].includes(dispute.status) ? [{
      label: 'Under admin review',
      time: dispute.updated_at,
      icon: 'MagnifyingGlassIcon',
      color: 'text-warning',
      bg: 'bg-warning/10',
      desc: 'Admin team is reviewing your case and evidence',
    }] : []),
    ...(dispute.status === 'escalated' ? [{
      label: 'Case escalated',
      time: dispute.updated_at,
      icon: 'ArrowUpCircleIcon',
      color: 'text-danger',
      bg: 'bg-danger/10',
      desc: 'Escalated for senior admin review',
    }] : []),
    ...(dispute.resolved_at ? [{
      label: dispute.status === 'resolved_refund' ? 'Refund issued to sender'
        : dispute.status === 'resolved_release'? 'Escrow released to traveler' :'Case closed',
      time: dispute.resolved_at,
      icon: 'CheckCircleIcon',
      color: 'text-success',
      bg: 'bg-success/10',
      desc: dispute.resolution_note ?? 'Resolution decision made by admin',
    }] : []),
  ];

  return (
    <Modal isOpen onClose={onClose} title={`Case #${dispute.id.slice(-6).toUpperCase()}`} size="lg">
      <div className="space-y-4">
        {/* Status bar */}
        <div className="flex flex-wrap items-center gap-3 p-3 bg-muted/40 rounded-lg">
          <StatusBadge label={STATUS_CONFIG[dispute.status].label} variant={STATUS_CONFIG[dispute.status].variant} dot />
          <span className={`text-xs font-600 ${PRIORITY_COLOR[dispute.priority]}`}>
            {dispute.priority.charAt(0).toUpperCase() + dispute.priority.slice(1)} Priority
          </span>
          <span className="text-xs text-muted-foreground ml-auto">
            Filed {new Date(dispute.created_at).toLocaleDateString()}
          </span>
        </div>

        {/* Pipeline */}
        <div className="p-4 border border-border rounded-xl bg-muted/20">
          <p className="text-xs font-700 text-muted-foreground mb-3 uppercase tracking-wide">Case Progress</p>
          <CaseStatusPipeline status={dispute.status} />
        </div>

        {/* Section tabs */}
        <div className="flex gap-1 border-b border-border">
          {(['details', 'timeline', 'notes'] as const).map(s => (
            <button
              key={s}
              onClick={() => setActiveSection(s)}
              className={`px-3 py-2 text-sm font-600 border-b-2 -mb-px capitalize transition-colors
                ${activeSection === s ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            >
              {s === 'notes' ? `Admin Notes (${notes.length})` : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Details section */}
        {activeSection === 'details' && (
          <div className="space-y-4">
            {/* Category */}
            {category && (
              <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={category.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-700 text-foreground">{category.label}</p>
                  <p className="text-xs text-muted-foreground">{category.description}</p>
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="p-3 bg-danger/5 border border-danger/20 rounded-lg">
              <p className="text-xs font-700 text-danger mb-1">Reported Issue</p>
              <p className="text-sm text-foreground">{dispute.reason}</p>
            </div>

            {/* Description */}
            {dispute.description && (
              <div className="p-3 border border-border rounded-lg">
                <p className="text-xs font-700 text-muted-foreground mb-1">Additional Details</p>
                <p className="text-sm text-foreground">{dispute.description}</p>
              </div>
            )}

            {/* Order info */}
            {(dispute.order_id || dispute.order_route) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border border-border rounded-lg">
                  <p className="text-[10px] text-muted-foreground mb-1">Order ID</p>
                  <p className="text-xs font-600 font-mono text-foreground">{dispute.order_id?.slice(-8).toUpperCase() ?? '—'}</p>
                </div>
                <div className="p-3 border border-border rounded-lg">
                  <p className="text-[10px] text-muted-foreground mb-1">Route</p>
                  <p className="text-xs font-600 text-foreground">{dispute.order_route ?? '—'}</p>
                </div>
              </div>
            )}

            {/* Amount */}
            <div className="p-3 border border-border rounded-lg flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Disputed Amount</p>
              <p className="text-base font-700 text-foreground">{dispute.currency} {dispute.amount.toLocaleString()}</p>
            </div>

            {/* Evidence */}
            {dispute.evidence_urls && dispute.evidence_urls.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-700 text-foreground">
                  Evidence ({dispute.evidence_urls.length} file{dispute.evidence_urls.length !== 1 ? 's' : ''})
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {dispute.evidence_urls.map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 border border-border rounded-lg hover:bg-muted transition-colors text-xs text-primary font-600 truncate"
                    >
                      <Icon name="PaperClipIcon" size={14} className="flex-shrink-0" />
                      File {i + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Resolution */}
            {isResolved && dispute.resolution_note && (
              <div className="p-3 bg-success/5 border border-success/20 rounded-lg">
                <p className="text-xs font-700 text-success mb-1">Admin Resolution</p>
                <p className="text-sm text-foreground">{dispute.resolution_note}</p>
                {dispute.resolved_at && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Resolved {new Date(dispute.resolved_at).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {/* Pending notice */}
            {!isResolved && (
              <div className="flex items-start gap-2 p-3 bg-warning/5 border border-warning/20 rounded-lg">
                <Icon name="ClockIcon" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                <p className="text-xs text-foreground">
                  Your dispute is in the admin review queue. You will be notified once a decision is made. Typical resolution time: 24–48 hours.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Timeline section */}
        {activeSection === 'timeline' && (
          <div className="space-y-1">
            {timelineEvents.map((event, i) => (
              <div key={i} className="flex items-start gap-3 relative">
                {i < timelineEvents.length - 1 && (
                  <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-border" />
                )}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${event.bg}`}>
                  <Icon name={event.icon as Parameters<typeof Icon>[0]['name']} size={14} className={event.color} />
                </div>
                <div className="flex-1 pb-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-700 text-foreground">{event.label}</p>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {new Date(event.time).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{event.desc}</p>
                </div>
              </div>
            ))}
            {!isResolved && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-dashed border-border flex items-center justify-center flex-shrink-0">
                  <Icon name="EllipsisHorizontalIcon" size={14} className="text-muted-foreground" />
                </div>
                <div className="flex-1 pb-4">
                  <p className="text-sm font-600 text-muted-foreground">Awaiting resolution…</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Admin will issue a decision after reviewing all evidence</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes section — public notes from admin */}
        {activeSection === 'notes' && (
          <div className="space-y-3">
            {loadingNotes ? (
              <div className="space-y-2">
                {[1, 2].map(i => <div key={i} className="h-16 rounded-lg bg-muted/40 animate-pulse" />)}
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-8 space-y-2">
                <Icon name="ChatBubbleLeftEllipsisIcon" size={28} className="text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">No admin notes yet. Check back after review begins.</p>
              </div>
            ) : (
              notes.map(note => (
                <div key={note.id} className="p-3 border border-border rounded-lg bg-muted/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-700 text-foreground">
                      {note.author?.full_name ?? 'Admin'}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(note.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-foreground">{note.note}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}


// ── Main Component ────────────────────────────────────────────────────────────
export default function DisputesContent() {
  const { user } = useAuth();
  const supabase = createClient();

  // ── Form state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'report' | 'history'>('report');
  const [selectedCategory, setSelectedCategory] = useState<DisputeCategory | null>(null);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [orderId, setOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [currency] = useState('KES');
  const [files, setFiles] = useState<{ name: string; preview: string; file: File }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ── AI Analysis state ───────────────────────────────────────────────────────
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysis | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysed, setAiAnalysed] = useState(false);

  // ── History state ───────────────────────────────────────────────────────────
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [linkedType, setLinkedType] = useState<'order' | 'delivery'>('order');

  // Reset AI analysis when reason/description changes significantly
  useEffect(() => {
    if (aiAnalysed) {
      setAiAnalysis(null);
      setAiAnalysed(false);
    }
  }, [reason, description]);

  // ── Fetch user's orders and deliveries for dropdown ──────────────────────────
  useEffect(() => {
    if (!user) return;
    supabase
      .from('orders').select('id, route, status').eq('sender_id', user.id).order('created_at', { ascending: false })
      .then(({ data }) => { if (data) setOrders(data as Order[]); });

    supabase
      .from('deliveries').select('id, route, status')
      .or(`sender_id.eq.${user.id},traveler_id.eq.${user.id}`)
      .order('created_at', { ascending: false })
      .then(({ data }) => { if (data) setDeliveries(data as Delivery[]); });
  }, [user]);

  // ── Fetch user's disputes ────────────────────────────────────────────────────
  const fetchDisputes = useCallback(async () => {
    if (!user) return;
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('disputes')
        .select('*, order:order_id(route)')
        .or(`sender_id.eq.${user.id},traveler_id.eq.${user.id},reported_by.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setDisputes(
          data.map((d: any) => ({
            ...d,
            order_route: d.order?.route ?? null,
          }))
        );
      }
    } finally {
      setLoadingHistory(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  // ── AI Dispute Analysis ──────────────────────────────────────────────────────
  const handleAIAnalyse = async () => {
    if (!reason.trim()) {
      toast.error('Please describe the issue first.');
      return;
    }
    setAiLoading(true);
    setAiAnalysis(null);
    try {
      const systemPrompt = `You are a dispute resolution specialist for MuyuRide, a peer-to-peer parcel delivery platform in Kenya. 
Analyze the user's dispute description and return a JSON object with:
- detectedCategory: one of "lost_parcel", "damage", "non_delivery", "fraud", "payment", "other" - confidence:"high", "medium", or "low"
- summary: 1-2 sentence assessment of the situation
- resolutionSteps: array of 3-5 actionable steps the user should take (be specific to the Kenyan delivery context)
- urgency: "critical", "high", "medium", or "low" - estimatedResolutionTime: e.g."24-48 hours", "3-5 business days"

Return ONLY valid JSON, no markdown, no explanation.`;

      const userMessage = `Dispute description: "${reason.trim()}"${description.trim() ? `\n\nAdditional details: "${description.trim()}"` : ''}`;

      const response = await getChatCompletion(
        'OPEN_AI',
        'gpt-4o-mini',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userMessage },
        ],
        { max_completion_tokens: 600 }
      );

      const content = response?.choices?.[0]?.message?.content ?? '';
      const parsed: AIAnalysis = JSON.parse(content);

      // Validate required fields
      const validCategories: DisputeCategory[] = ['lost_parcel', 'damage', 'non_delivery', 'fraud', 'payment', 'other'];
      if (!validCategories.includes(parsed.detectedCategory)) {
        parsed.detectedCategory = 'other';
      }

      setAiAnalysis(parsed);
      setAiAnalysed(true);
    } catch (err: any) {
      toast.error('AI analysis failed. Please select a category manually.');
    } finally {
      setAiLoading(false);
    }
  };

  // ── File handlers ────────────────────────────────────────────────────────────
  const handleAddFiles = (fileList: FileList) => {
    const remaining = 5 - files.length;
    if (remaining <= 0) { toast.error('Maximum 5 files allowed.'); return; }
    const toAdd = Array.from(fileList).slice(0, remaining);
    const newEntries = toAdd.map(f => ({
      name: f.name,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : '',
      file: f,
    }));
    setFiles(prev => [...prev, ...newEntries]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => {
      const copy = [...prev];
      if (copy[index].preview) URL.revokeObjectURL(copy[index].preview);
      copy.splice(index, 1);
      return copy;
    });
  };

  // ── Upload evidence files ────────────────────────────────────────────────────
  const uploadEvidence = async (): Promise<string[]> => {
    if (!user || files.length === 0) return [];
    setUploading(true);
    const urls: string[] = [];
    for (const f of files) {
      const ext = f.name.split('.').pop();
      const path = `${user.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage
        .from('dispute-evidence')
        .upload(path, f.file, { upsert: false });
      if (!error) {
        const { data: urlData } = supabase.storage.from('dispute-evidence').getPublicUrl(path);
        urls.push(urlData.publicUrl);
      }
    }
    setUploading(false);
    return urls;
  };

  // ── Submit dispute ───────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) { toast.error('Please select a dispute category.'); return; }
    if (!reason.trim()) { toast.error('Please describe the issue briefly.'); return; }
    if (!user) return;

    setSubmitting(true);
    try {
      const evidenceUrls = await uploadEvidence();

      const payload: Record<string, unknown> = {
        category: selectedCategory,
        reason: reason.trim(),
        description: description.trim() || null,
        evidence_urls: evidenceUrls,
        reported_by: user.id,
        amount: parseFloat(amount) || 0,
        currency,
        status: 'open',
        priority: selectedCategory === 'fraud' ? 'critical' : selectedCategory === 'lost_parcel' ? 'high' : 'medium',
      };

      if (orderId) payload.order_id = orderId;

      const { error } = await supabase.from('disputes').insert(payload);

      if (error) throw error;

      toast.success('Dispute submitted successfully. Our team will review it within 24–48 hours.');
      setSelectedCategory(null);
      setReason('');
      setDescription('');
      setOrderId('');
      setAmount('');
      setFiles([]);
      setAiAnalysis(null);
      setAiAnalysed(false);
      setActiveTab('history');
      fetchDisputes();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to submit dispute. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  const openCount = disputes.filter(d => !['resolved_refund', 'resolved_release', 'closed'].includes(d.status)).length;
  const resolvedCount = disputes.filter(d => ['resolved_refund', 'resolved_release', 'closed'].includes(d.status)).length;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-danger/10 flex items-center justify-center flex-shrink-0">
          <Icon name="ShieldExclamationIcon" size={22} className="text-danger" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-heading font-800 text-foreground">Disputes & Claims</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            File claims for lost parcels, payment issues, or non-delivery. Track case status through admin review.
          </p>
        </div>
      </div>

      {/* Stats strip */}
      {disputes.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-xl border border-border bg-muted/20 text-center">
            <p className="text-xl font-700 text-foreground tabular-nums">{disputes.length}</p>
            <p className="text-[11px] text-muted-foreground">Total Cases</p>
          </div>
          <div className="p-3 rounded-xl border border-danger/20 bg-danger/5 text-center">
            <p className="text-xl font-700 text-danger tabular-nums">{openCount}</p>
            <p className="text-[11px] text-muted-foreground">Active</p>
          </div>
          <div className="p-3 rounded-xl border border-success/20 bg-success/5 text-center">
            <p className="text-xl font-700 text-success tabular-nums">{resolvedCount}</p>
            <p className="text-[11px] text-muted-foreground">Resolved</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {(['report', 'history'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-600 border-b-2 -mb-px capitalize transition-colors
              ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {tab === 'report' ? 'File a Claim' : `My Cases${disputes.length > 0 ? ` (${disputes.length})` : ''}`}
          </button>
        ))}
      </div>

      {/* ── Report Tab ── */}
      {activeTab === 'report' && (
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* ── AI Analysis Trigger ── */}
          <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-3">
            <div className="flex items-center gap-2">
              <Icon name="SparklesIcon" size={16} className="text-primary" />
              <p className="text-sm font-700 text-foreground">AI-Powered Dispute Detection</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Describe your issue below, then let AI auto-classify the dispute type and suggest resolution steps.
            </p>

            {/* Brief reason */}
            <div className="space-y-1.5">
              <label className="text-xs font-700 text-foreground">
                Brief Description <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. Parcel not delivered — traveler claims handover but I never received it"
                maxLength={200}
                className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <p className="text-[11px] text-muted-foreground text-right">{reason.length}/200</p>
            </div>

            {/* Full details */}
            <div className="space-y-1.5">
              <label className="text-xs font-700 text-foreground">Full Details (optional)</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Provide any additional context — dates, times, communications, what happened step by step…"
                rows={3}
                className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />
            </div>

            {/* Analyse button */}
            {reason.trim().length >= 10 && !aiAnalysis && (
              <button
                type="button"
                onClick={handleAIAnalyse}
                disabled={aiLoading}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-600 hover:opacity-90 disabled:opacity-60 transition-opacity"
              >
                {aiLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Analysing…
                  </>
                ) : (
                  <>
                    <Icon name="SparklesIcon" size={15} />
                    Analyse with AI
                  </>
                )}
              </button>
            )}

            {/* AI Result */}
            {aiAnalysis && (
              <AIAnalysisPanel
                analysis={aiAnalysis}
                onApplyCategory={(cat) => {
                  setSelectedCategory(cat);
                  toast.success(`Category set to "${CATEGORY_OPTIONS.find(c => c.value === cat)?.label}"`);
                }}
                onDismiss={() => { setAiAnalysis(null); setAiAnalysed(false); }}
              />
            )}
          </div>

          {/* Category selection */}
          <div className="space-y-3">
            <label className="text-sm font-700 text-foreground">
              Claim Type <span className="text-danger">*</span>
              {aiAnalysis && (
                <span className="ml-2 text-xs font-400 text-primary">
                  (AI suggested: {CATEGORY_OPTIONS.find(c => c.value === aiAnalysis.detectedCategory)?.label})
                </span>
              )}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CATEGORY_OPTIONS.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all
                    ${selectedCategory === cat.value
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                      : aiAnalysis?.detectedCategory === cat.value
                        ? 'border-primary/40 bg-primary/3' :'border-border hover:border-primary/40 hover:bg-muted/30'
                    }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5
                    ${selectedCategory === cat.value ? 'bg-primary/15' : 'bg-muted'}`}>
                    <Icon
                      name={cat.icon as Parameters<typeof Icon>[0]['name']}
                      size={16}
                      className={selectedCategory === cat.value ? 'text-primary' : 'text-muted-foreground'}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className={`text-sm font-700 ${selectedCategory === cat.value ? 'text-primary' : 'text-foreground'}`}>
                        {cat.label}
                      </p>
                      {aiAnalysis?.detectedCategory === cat.value && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-700">AI</span>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{cat.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Linked order/delivery */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-700 text-foreground">Linked Transaction (optional)</label>
              <div className="flex gap-1 text-xs">
                <button
                  type="button"
                  onClick={() => { setLinkedType('order'); setOrderId(''); }}
                  className={`px-2 py-1 rounded font-600 transition-colors ${linkedType === 'order' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Order
                </button>
                <button
                  type="button"
                  onClick={() => { setLinkedType('delivery'); setOrderId(''); }}
                  className={`px-2 py-1 rounded font-600 transition-colors ${linkedType === 'delivery' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  Delivery
                </button>
              </div>
            </div>
            <select
              value={orderId}
              onChange={e => setOrderId(e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">— Select a {linkedType} —</option>
              {linkedType === 'order'
                ? orders.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.route} · #{o.id.slice(-6).toUpperCase()}
                    </option>
                  ))
                : deliveries.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.route} · #{d.id.slice(-6).toUpperCase()} · {d.status}
                    </option>
                  ))
              }
            </select>
          </div>

          {/* Disputed amount */}
          <div className="space-y-2">
            <label className="text-sm font-700 text-foreground">Disputed Amount (KES)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-600">KES</span>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full pl-12 pr-3 py-2.5 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          {/* Evidence upload */}
          <div className="space-y-2">
            <label className="text-sm font-700 text-foreground">Evidence Attachments</label>
            <p className="text-xs text-muted-foreground">
              Attach photos, screenshots, PDFs, or short videos that support your claim. Strong evidence speeds up resolution.
            </p>
            <FileUploadZone
              files={files}
              onAdd={handleAddFiles}
              onRemove={handleRemoveFile}
              uploading={uploading || submitting}
            />
          </div>

          {/* Info notice */}
          <div className="flex items-start gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <Icon name="InformationCircleIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-foreground">
              Claims are reviewed by our admin team within 24–48 hours. Both parties will be contacted for their account.
              Fraudulent or false reports may result in account suspension.
            </p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || uploading || !selectedCategory || !reason.trim()}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-danger text-white rounded-xl font-700 text-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {submitting || uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {uploading ? 'Uploading evidence…' : 'Submitting claim…'}
              </>
            ) : (
              <>
                <Icon name="ShieldExclamationIcon" size={16} />
                Submit Claim
              </>
            )}
          </button>
        </form>
      )}

      {/* ── History Tab ── */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {loadingHistory ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-28 rounded-xl bg-muted/40 animate-pulse" />
              ))}
            </div>
          ) : disputes.length === 0 ? (
            <div className="text-center py-16 space-y-3">
              <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto">
                <Icon name="ShieldCheckIcon" size={26} className="text-muted-foreground" />
              </div>
              <p className="text-base font-700 text-foreground">No cases filed</p>
              <p className="text-sm text-muted-foreground">You have not reported any issues yet.</p>
              <button
                onClick={() => setActiveTab('report')}
                className="mt-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-600 hover:opacity-90 transition-opacity"
              >
                File a Claim
              </button>
            </div>
          ) : (
            disputes.map(d => {
              const cat = CATEGORY_OPTIONS.find(c => c.value === d.category);
              const cfg = STATUS_CONFIG[d.status];
              const isResolved = ['resolved_refund', 'resolved_release', 'closed'].includes(d.status);
              return (
                <button
                  key={d.id}
                  onClick={() => setSelectedDispute(d)}
                  className="w-full text-left p-4 border border-border rounded-xl hover:border-primary/40 hover:bg-muted/20 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                      ${isResolved ? 'bg-success/10' : 'bg-muted'}`}>
                      {cat && (
                        <Icon
                          name={cat.icon as Parameters<typeof Icon>[0]['name']}
                          size={18}
                          className={isResolved ? 'text-success' : 'text-muted-foreground group-hover:text-primary transition-colors'}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-700 text-foreground">{cat?.label ?? d.category}</span>
                        <StatusBadge label={cfg.label} variant={cfg.variant} dot />
                        <span className={`text-xs font-600 ${PRIORITY_COLOR[d.priority]}`}>
                          {d.priority.charAt(0).toUpperCase() + d.priority.slice(1)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{d.reason}</p>

                      {/* Mini pipeline */}
                      <div className="mt-2.5 flex items-center gap-1">
                        {PIPELINE_STEPS.map((step, i) => {
                          const stepDone = i <= cfg.step;
                          return (
                            <React.Fragment key={i}>
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 transition-colors
                                ${stepDone ? (isResolved ? 'bg-success' : 'bg-primary') : 'bg-border'}`} />
                              {i < PIPELINE_STEPS.length - 1 && (
                                <div className={`flex-1 h-0.5 transition-colors
                                  ${i < cfg.step ? (isResolved ? 'bg-success' : 'bg-primary') : 'bg-border'}`} />
                              )}
                            </React.Fragment>
                          );
                        })}
                        <span className={`ml-1 text-[10px] font-600 ${isResolved ? 'text-success' : 'text-primary'}`}>
                          {cfg.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                        <span>#{d.id.slice(-6).toUpperCase()}</span>
                        {d.order_route && <span>· {d.order_route}</span>}
                        {d.amount > 0 && <span>· KES {d.amount.toLocaleString()}</span>}
                        <span className="ml-auto">{new Date(d.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}

      {/* Detail modal */}
      {selectedDispute && (
        <DisputeDetailModal
          dispute={selectedDispute}
          onClose={() => setSelectedDispute(null)}
        />
      )}
    </div>
  );
}
